import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Note as NoteIcon,
  Plus,
  Trash,
  CheckCircle,
  Circle,
  PencilSimple,
  Check as CheckIcon,
  X as XIcon,
  Truck as TruckIcon,
  CaretUpDown,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { fetchNotes, createNote, updateNote, deleteNote, fetchTrucks } from "@/lib/api";
import useAutoRefresh from "@/lib/useAutoRefresh";

const formatRelative = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `il y a ${h} h`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [input, setInput] = useState("");
  const [selectedTruckId, setSelectedTruckId] = useState("");
  const [filter, setFilter] = useState("active"); // active | all | done
  const [editing, setEditing] = useState({ id: null, text: "" });
  const inputRef = useRef(null);

  const eligibleTrucks = useMemo(
    () =>
      trucks
        .filter((t) => t.status !== "rendu" && t.status !== "inactif")
        .sort((a, b) =>
          (a.license_plate || "").localeCompare(b.license_plate || "", "fr", { sensitivity: "base" })
        ),
    [trucks]
  );
  const selectedTruck = useMemo(
    () => eligibleTrucks.find((t) => t.id === selectedTruckId) || null,
    [eligibleTrucks, selectedTruckId]
  );

  const load = async ({ silent = false } = {}) => {
    try {
      const [n, t] = await Promise.all([fetchNotes(), fetchTrucks()]);
      setNotes(n);
      setTrucks(t);
    } catch {
      if (!silent) toast.error("Impossible de charger les notes");
    }
  };
  useEffect(() => {
    load();
  }, []);
  useAutoRefresh(() => load({ silent: true }), 15000);

  const addNote = async () => {
    const txt = input.trim();
    if (!txt) return;
    try {
      const n = await createNote(txt, selectedTruckId || null);
      setNotes((prev) => [n, ...prev]);
      setInput("");
      setSelectedTruckId("");
      inputRef.current?.focus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const toggleDone = async (n) => {
    try {
      const updated = await updateNote(n.id, { done: !n.done });
      setNotes((prev) => prev.map((x) => (x.id === n.id ? updated : x)));
    } catch {
      toast.error("Erreur");
    }
  };

  const startEdit = (n) => setEditing({ id: n.id, text: n.text });
  const cancelEdit = () => setEditing({ id: null, text: "" });
  const saveEdit = async () => {
    if (!editing.id) return;
    const txt = editing.text.trim();
    if (!txt) return toast.error("Texte requis");
    try {
      const updated = await updateNote(editing.id, { text: txt });
      setNotes((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      cancelEdit();
    } catch {
      toast.error("Erreur");
    }
  };

  const remove = async (n) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await deleteNote(n.id);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch {
      toast.error("Erreur");
    }
  };

  const filtered = useMemo(() => {
    if (filter === "active") return notes.filter((n) => !n.done);
    if (filter === "done") return notes.filter((n) => n.done);
    return notes;
  }, [notes, filter]);

  const counts = {
    all: notes.length,
    active: notes.filter((n) => !n.done).length,
    done: notes.filter((n) => n.done).length,
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-6" data-testid="notes-page">
      <div className="mb-6">
        <div className="label-tag mb-1">NY TRANS</div>
        <h1 className="text-4xl md:text-5xl serif-display flex items-center gap-3">
          <NoteIcon size={38} weight="duotone" className="text-amber-600" />
          Rappels & Notes
        </h1>
        <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">
          Partagés pour tous · Cochez une note pour la marquer comme faite
        </p>
      </div>

      {/* Input */}
      <div className="border-2 border-zinc-900 bg-white p-3 mb-4">
        <NoteTruckPicker
          trucks={eligibleTrucks}
          value={selectedTruckId}
          onChange={setSelectedTruckId}
        />
        <div className="flex items-end gap-2 mt-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                addNote();
              }
            }}
            placeholder={
              selectedTruck
                ? `Rappel pour ${selectedTruck.license_plate} — ex : changer les pneus`
                : "Ex : le 824 prendre RDV pour des pneus"
            }
            rows={2}
            className="rounded-none border-zinc-900 flex-1 resize-none"
            data-testid="note-input"
          />
          <Button
            onClick={addNote}
            className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-full min-h-[64px] gap-1"
            data-testid="note-add"
          >
            <Plus size={14} weight="bold" /> Ajouter
          </Button>
        </div>
        <div className="text-[10px] text-zinc-400 mt-1 uppercase tracking-widest">
          Astuce : Ctrl/⌘ + Entrée pour ajouter · Camion optionnel — traçable dans son audit
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1 mb-3" data-testid="notes-filters">
        {[
          { k: "active", l: `À faire · ${counts.active}` },
          { k: "done", l: `Terminées · ${counts.done}` },
          { k: "all", l: `Toutes · ${counts.all}` },
        ].map(({ k, l }) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border-2 ${
              filter === k
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
            }`}
            data-testid={`notes-filter-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
          {filter === "active" ? "Rien à faire pour le moment." : "Aucune note."}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="notes-list">
          {filtered.map((n) => {
            const isEditing = editing.id === n.id;
            return (
              <li
                key={n.id}
                className={`border-2 flex items-start gap-2 p-3 ${
                  n.done ? "border-zinc-200 bg-zinc-50" : "border-zinc-900 bg-white"
                }`}
                data-testid={`note-${n.id}`}
              >
                <button
                  onClick={() => toggleDone(n)}
                  className="shrink-0 mt-0.5"
                  title={n.done ? "Rouvrir cette note" : "Marquer comme faite"}
                  data-testid={`note-toggle-${n.id}`}
                >
                  {n.done ? (
                    <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                  ) : (
                    <Circle size={22} weight="bold" className="text-zinc-400 hover:text-zinc-800" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <div className="flex flex-col gap-1">
                      <Textarea
                        value={editing.text}
                        onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                        rows={2}
                        className="rounded-none border-zinc-900 resize-none"
                        data-testid={`note-edit-input-${n.id}`}
                      />
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={cancelEdit}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border-2 border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-1"
                        >
                          <XIcon size={11} weight="bold" /> Annuler
                        </button>
                        <button
                          onClick={saveEdit}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-1"
                          data-testid={`note-edit-save-${n.id}`}
                        >
                          <CheckIcon size={11} weight="bold" /> Enregistrer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className={`text-sm whitespace-pre-wrap ${n.done ? "line-through text-zinc-500" : ""}`}
                      >
                        {n.text}
                      </div>
                      <div className="text-[10px] text-zinc-400 uppercase tracking-widest mt-1 flex items-center gap-2 flex-wrap">
                        <span>
                          {formatRelative(n.created_at)}
                          {n.created_by && ` · ${n.created_by.split("@")[0]}`}
                          {n.done && n.done_at && ` · fait ${formatRelative(n.done_at)}`}
                        </span>
                        {n.truck_plate && n.truck_id && (
                          <Link
                            to={`/trucks/${n.truck_id}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 font-bold mono border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700"
                            title="Ouvrir la fiche camion"
                            data-testid={`note-truck-link-${n.id}`}
                          >
                            <TruckIcon size={10} weight="bold" /> {n.truck_plate}
                          </Link>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => startEdit(n)}
                      className="text-zinc-400 hover:text-zinc-900 p-1"
                      title="Modifier"
                      data-testid={`note-edit-${n.id}`}
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => remove(n)}
                      className="text-zinc-300 hover:text-red-600 p-1"
                      title="Supprimer"
                      data-testid={`note-delete-${n.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ===================== Truck picker (excludes rendu + inactif) ===================== */

function NoteTruckPicker({ trucks, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => trucks.find((t) => t.id === value) || null, [trucks, value]);

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`flex-1 h-9 px-2 border-2 text-sm inline-flex items-center justify-between gap-1 text-left ${
              selected ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-900"
            }`}
            data-testid="note-truck-picker"
          >
            <span className="inline-flex items-center gap-2 min-w-0 truncate">
              <TruckIcon size={14} weight="bold" className="shrink-0" />
              {selected ? (
                <>
                  <span className="font-bold mono">{selected.license_plate}</span>
                  {selected.brand && <span className="text-[10px] opacity-70 truncate">{selected.brand}</span>}
                </>
              ) : (
                <span className="text-[11px] uppercase tracking-widest">Associer un camion (optionnel)</span>
              )}
            </span>
            <CaretUpDown size={13} weight="bold" className="shrink-0 opacity-70" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[360px]" align="start">
          <Command className="rounded-none">
            <CommandInput placeholder="Plaque, marque, modèle…" className="text-sm" />
            <CommandList>
              <CommandEmpty>Aucun camion.</CommandEmpty>
              <CommandGroup heading="Camions actifs">
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="rounded-none text-sm italic text-zinc-500"
                  data-testid="note-truck-none"
                >
                  — Aucun camion —
                </CommandItem>
                {trucks.map((t) => {
                  const search = `${t.license_plate} ${t.brand || ""} ${t.model || ""}`.toLowerCase();
                  const isSelected = t.id === value;
                  return (
                    <CommandItem
                      key={t.id}
                      value={search}
                      onSelect={() => {
                        onChange(t.id);
                        setOpen(false);
                      }}
                      className="rounded-none text-sm flex items-center gap-2"
                      data-testid={`note-truck-option-${t.id}`}
                    >
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                          t.status === "panne" ? "bg-amber-500" : "bg-emerald-500"
                        }`}
                      />
                      <span className={`font-bold mono uppercase ${isSelected ? "text-emerald-600" : ""}`}>
                        {t.license_plate}
                      </span>
                      <span className="text-xs text-zinc-500 truncate">
                        {(t.brand || "").toUpperCase()} · {(t.site || "").toUpperCase()}
                      </span>
                      {t.status === "panne" && (
                        <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-amber-700">
                          En panne
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-zinc-400 hover:text-red-600 shrink-0"
          title="Retirer l'association camion"
          data-testid="note-truck-clear"
        >
          <XIcon size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}

